import { useEffect, useRef } from "react";
import Hls from "hls.js";
import type { PluginListenerHandle } from "@capacitor/core";
import type { StreamFormat, StationKind } from "./radio";
import { findAndParseId3, splitArtistTitle, type Id3Frames } from "./id3";
import { IcyRadio, isNativeRadio, parseIcyTitle } from "./nativeRadio";

export type LiveMetadata = {
  artist?: string;
  title?: string;
  album?: string;
};

type Args = {
  audioRef: React.RefObject<HTMLAudioElement>;
  url: string | null;
  format: StreamFormat | null;
  playing: boolean;
  kind?: StationKind | null;
  onError?: (msg: string) => void;
  /** Fires when in-band ID3 metadata arrives from the live stream. */
  onLiveMetadata?: (meta: LiveMetadata) => void;
};

/**
 * useAudioSource — attach a stream URL to an <audio> tag, choosing between
 * native playback and hls.js based on `format` and browser capability.
 *
 * Why hls.js: HLS is the format DR's live radio ships in, and only Safari
 * plays it natively. On Chrome / Firefox / WebView we hand the URL to
 * hls.js which feeds chunks into MediaSource. Native HLS (Safari/iOS,
 * Capacitor's WKWebView) is preferred when available — lower battery.
 */
export function useAudioSource({ audioRef, url, format, playing, kind, onError, onLiveMetadata }: Args) {
  const hlsRef = useRef<Hls | null>(null);
  // Keep callback stable across renders so the metadata listeners don't churn.
  const metaCbRef = useRef(onLiveMetadata);
  metaCbRef.current = onLiveMetadata;
  const errCbRef = useRef(onError);
  errCbRef.current = onError;

  // On the native Android build, the IcyRadio ExoPlayer plugin owns
  // playback + metadata (see nativeRadio.ts / IcyRadioPlugin.java). The
  // <audio>/hls.js path below is web-only.
  const native = isNativeRadio();
  const loadedUrlRef = useRef<string | null>(null);

  // ── NATIVE: ExoPlayer event listeners (mounted once) ──
  useEffect(() => {
    if (!native) return;
    let metaH: PluginListenerHandle | null = null;
    let stateH: PluginListenerHandle | null = null;
    let active = true;
    (async () => {
      metaH = await IcyRadio.addListener("metadata", (d) => {
        const { artist, title } = parseIcyTitle(d.title);
        if (title || artist) {
          metaCbRef.current?.({ artist: artist ?? undefined, title: title ?? undefined });
        }
      });
      stateH = await IcyRadio.addListener("state", (d) => {
        if (d.state === "error") errCbRef.current?.(d.error || "playback error");
      });
      if (!active) { metaH?.remove(); stateH?.remove(); }
    })();
    return () => { active = false; metaH?.remove(); stateH?.remove(); };
  }, [native]);

  // ── NATIVE: playback control (react to url / playing) ──
  useEffect(() => {
    if (!native) return;
    if (!url) {
      IcyRadio.stop().catch(() => {});
      loadedUrlRef.current = null;
      return;
    }
    if (playing) {
      if (loadedUrlRef.current !== url) {
        loadedUrlRef.current = url;
        IcyRadio.play({ url }).catch((e) => errCbRef.current?.(String(e?.message || e)));
      } else {
        IcyRadio.resume().catch(() => {});
      }
    } else {
      IcyRadio.pause().catch(() => {});
    }
  }, [native, url, playing]);

  useEffect(() => {
    if (native) return; // native path handles playback above
    const audio = audioRef.current;
    if (!audio) return;
    // tear down any previous hls instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (!url) { audio.pause(); audio.removeAttribute("src"); return; }

    const isHls = format === "hls" || /\.m3u8(\?|$)/i.test(url);
    const canPlayNative = audio.canPlayType("application/vnd.apple.mpegurl") !== "";

    /** Convert ID3 frames into a clean { artist, title } pair. */
    const emitFromId3 = (frames: Id3Frames | null) => {
      if (!frames) return;
      let title = frames.title;
      let artist = frames.artist;
      // Many radios stuff the whole "Artist - Title" into a TXXX:StreamTitle
      // OR into TIT2 alone. Split when there's no TPE1.
      if (!artist && frames.streamTitle) {
        const split = splitArtistTitle(frames.streamTitle);
        artist = split.artist;
        title = split.title;
      } else if (!artist && title && /\s+[-–]\s+/.test(title)) {
        const split = splitArtistTitle(title);
        artist = split.artist;
        title = split.title;
      }
      if (!title && !artist) return;
      metaCbRef.current?.({ title, artist, album: frames.album });
    };

    if (isHls && !canPlayNative) {
      if (!Hls.isSupported()) {
        onError?.("HLS not supported in this browser");
        return;
      }
      const hls = new Hls({
        lowLatencyMode: false,
        enableWorker: true,
        // live radio: keep the buffer short
        liveSyncDurationCount: 3,
        // Memory caps — without these hls.js retains every played
        // segment (backBufferLength defaults to Infinity), which over
        // 30 min of streaming grows to hundreds of MB and was the
        // root of the long-session "music chops + UI hangs" on Android
        // WebView. 30 s of each direction is plenty for live radio.
        backBufferLength: 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Cap forward-buffer bytes too — defaults to 60 MB; halve it.
        maxBufferSize: 30 * 1000 * 1000,
      });
      hls.loadSource(url);
      hls.attachMedia(audio);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) onError?.(`HLS: ${data.type}/${data.details}`);
      });
      // In-band ID3 metadata — broadcasted by DR (and most radio) alongside audio.
      hls.on(Hls.Events.FRAG_PARSING_METADATA, (_e, data) => {
        for (const sample of data.samples || []) {
          const buf = sample.data instanceof Uint8Array
            ? sample.data
            : new Uint8Array(sample.data as ArrayBuffer);
          emitFromId3(findAndParseId3(buf));
        }
      });
      hlsRef.current = hls;
    } else {
      audio.src = url;
      // Native HLS path (Safari / iOS Capacitor WebView): metadata arrives as
      // text-track cues on the <audio> element.
      const onCueChange = (ev: Event) => {
        const track = ev.target as TextTrack;
        const cue = track.activeCues?.[0] as (TextTrackCue & { value?: { key?: string; data?: string | Uint8Array } }) | undefined;
        const value = cue?.value;
        if (!value) return;
        const key = value.key;
        const dataField = value.data;
        const str = typeof dataField === "string" ? dataField : null;
        if (str && (key === "TIT2" || key === "title")) emitFromId3({ title: str });
        else if (str && (key === "TPE1" || key === "artist")) emitFromId3({ artist: str });
        else if (str && key === "TXXX") emitFromId3({ streamTitle: str });
      };
      const onAddTrack = (e: Event) => {
        const tracks = (e.target as HTMLAudioElement).textTracks;
        for (let i = 0; i < tracks.length; i++) {
          const t = tracks[i];
          if (t.kind === "metadata") {
            t.mode = "hidden";
            t.addEventListener("cuechange", onCueChange);
          }
        }
      };
      audio.textTracks.addEventListener("addtrack", onAddTrack);
      // ensure existing tracks are wired immediately
      onAddTrack({ target: audio } as unknown as Event);
    }
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [audioRef, url, format, onError]);

  // jump to live on play if it's radio
  useEffect(() => {
    if (native) return;
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => {
      if (kind === "radio") {
        if (hlsRef.current) {
          const live = hlsRef.current.liveSyncPosition;
          if (live !== null && isFinite(live)) audio.currentTime = live;
          else if (isFinite(audio.duration)) audio.currentTime = audio.duration;
        } else if (isFinite(audio.duration) && audio.duration > 0) {
          audio.currentTime = audio.duration;
        }
      }
    };
    audio.addEventListener("play", onPlay);
    return () => audio.removeEventListener("play", onPlay);
  }, [audioRef, kind]);

  // play/pause toggle
  useEffect(() => {
    if (native) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.play().catch((e) => onError?.(String(e?.message || e)));
    } else {
      audio.pause();
    }
  }, [audioRef, playing, url, onError, native]);
}
